import * as FinancialTypes from '../types/unified-financial.ts';

// Alias para manter o código abaixo funcionando sem grandes mudanças
type UnifiedPatient = FinancialTypes.UnifiedPatient;
type PackageInfo = FinancialTypes.PackageInfo;

/**

 */
export class BillingPeriodUtils {
  private static parseFlexibleDate(dateStr: string | undefined): Date | null {
    if (!dateStr) return null;

    // Normalizar separadores e remover espaços extras
    const normalized = dateStr.replace(/\\/g, '/').trim();

    // 1) Tentativa direta com Date.parse (ISO, RFC, etc)
    const direct = Date.parse(normalized);
    if (!Number.isNaN(direct)) {
      const parsed = new Date(direct);
      if (!Number.isNaN(parsed.getTime())) {
        parsed.setHours(0, 0, 0, 0);
        return parsed;
      }
    }

    // 2) Formatos comuns brasileiros (DD/MM/YYYY ou DD-MM-YYYY)
    const delimiter = normalized.includes('/') ? '/' : normalized.includes('-') ? '-' : null;
    if (delimiter) {
      const parts = normalized.split(delimiter);
      if (parts.length === 3) {
        const [d, m, y] = parts.map(part => parseInt(part, 10));
        if (!Number.isNaN(d) && !Number.isNaN(m) && !Number.isNaN(y)) {
          const candidate = new Date(y, m - 1, d, 0, 0, 0, 0);
          if (!Number.isNaN(candidate.getTime())) {
            return candidate;
          }
        }
      }
    }

    console.warn(`⚠️ Não foi possível converter a data "${dateStr}"`);
    return null;
  }

// REPLACE
  private static getValidityMonths(packageInfo: PackageInfo): number {
    switch (packageInfo.packageValidityType) {
      case '2_MONTHS':
        return 2;
      case '3_MONTHS':
        return 3;
      case 'CUSTOM':
        return packageInfo.packageCustomValidityMonths && packageInfo.packageCustomValidityMonths > 0
          ? packageInfo.packageCustomValidityMonths
          : 1;
      default:
        return 1;
    }
  }

  private static addMonthClamped(base: Date, months: number, baseDay: number): Date {
    const result = new Date(base);
    result.setHours(0, 0, 0, 0);
    result.setDate(1);
    result.setMonth(result.getMonth() + months);
    const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
    result.setDate(Math.min(baseDay, lastDay));
    return result;
  }

  private static getNextCycleStart(currentStart: Date, monthsToAdd: number, baseDay: number): Date {
    let next = this.addMonthClamped(currentStart, monthsToAdd, baseDay);
    if (next.getDate() < baseDay) {
      next.setDate(next.getDate() + 1);
    }
    next.setHours(0, 0, 0, 0);
    return next;
  }

  private static getPreviousCycleStart(currentStart: Date, monthsToAdd: number, baseDay: number): Date {
    let prev = this.addMonthClamped(currentStart, -monthsToAdd, baseDay);
    if (prev.getDate() < baseDay) {
      prev.setDate(prev.getDate() + 1);
    }
    prev.setHours(0, 0, 0, 0);
    return prev;
  }

  /**
   * Calcula o período de faturamento baseado no pacote do paciente
   * Para pacientes com pacote: usa dia da paymentDate como referência
   * Para pacientes sem pacote: usa mês calendário completo
   */
// REPLACE
  static calculateBillingPeriod(patient: UnifiedPatient, month: string): { startDate: Date; endDate: Date } {
    const [year, monthNum] = month.split("-").map(Number);

    // ✅ VALIDAÇÃO: Verificar se ano e mês são válidos
    if (!year || !monthNum || monthNum < 1 || monthNum > 12) {
      console.error(`❌ Mês inválido: ${month}`);
      throw new Error(`Mês inválido: ${month}. Use formato YYYY-MM`);
    }

    // Se paciente tem pacote e data de pagamento
    if (patient.financialProfile.hasPackage && patient.financialProfile.packageInfo?.packagePaymentDate) {
      const packageInfo = patient.financialProfile.packageInfo;
      const paymentBase = this.parseFlexibleDate(packageInfo.packagePaymentDate);

      if (!paymentBase) {
        console.warn(`⚠️ [${patient.personalData.name}] Não foi possível interpretar packagePaymentDate (${packageInfo.packagePaymentDate}). Usando período mensal padrão.`);
      } else {
        const monthsToAdd = this.getValidityMonths(packageInfo);
        const baseDay = paymentBase.getDate();
        const { startDate: monthStart, endDate: monthEnd } = this.calculateMonthlyPeriod(month);

        let cycleStart = new Date(paymentBase);
        cycleStart.setHours(0, 0, 0, 0);

        // Ajustar ciclos para cobrir o mês solicitado
        while (cycleStart > monthStart) {
          const prev = this.getPreviousCycleStart(cycleStart, monthsToAdd, baseDay);
          if (prev.getTime() === cycleStart.getTime()) break;
          cycleStart = prev;
        }

        let nextCycleStart = this.getNextCycleStart(cycleStart, monthsToAdd, baseDay);
        while (nextCycleStart <= monthStart) {
          cycleStart = nextCycleStart;
          nextCycleStart = this.getNextCycleStart(cycleStart, monthsToAdd, baseDay);
        }

        // Garantir que o período intersecta o mês
        if (cycleStart > monthEnd) {
          cycleStart = this.getPreviousCycleStart(cycleStart, monthsToAdd, baseDay);
          nextCycleStart = this.getNextCycleStart(cycleStart, monthsToAdd, baseDay);
        }

        let endExclusive: Date | null = null;
        if (packageInfo.packageRenewalDate) {
          const parsedRenewal = this.parseFlexibleDate(packageInfo.packageRenewalDate);
          if (parsedRenewal) {
            endExclusive = parsedRenewal;
          }
        }
        if (!endExclusive || endExclusive <= cycleStart) {
          endExclusive = nextCycleStart;
        }

        const startDate = new Date(cycleStart);
        startDate.setHours(0, 0, 0, 0);

        const endDate = new Date(endExclusive.getTime() - 1);
        endDate.setHours(23, 59, 59, 999);

        if (endDate < monthStart || startDate > monthEnd) {
          //console.warn(`⚠️ [${patient.personalData.name}] Período calculado (${this.formatBillingPeriod(startDate, endDate)}) não intersecta ${month}. Usando período padrão.`);
        } else {
          //console.log(`📅 [${patient.personalData.name}] Período de faturamento calculado:`);
          //console.log(`   - Mês solicitado: ${month}`);
          //console.log(`   - PaymentDate: ${packageInfo.packagePaymentDate}`);
          if (packageInfo.packageRenewalDate) {
            //console.log(`   - RenewalDate informado: ${packageInfo.packageRenewalDate}`);
          }
          //console.log(`   - Período calculado: ${this.formatBillingPeriod(startDate, endDate)}`);

          return { startDate, endDate };
        }
      }
    }

    // Pacientes sem pacote: período mensal padrão (1 ao último dia do mês)
    console.log(`📅 [${patient.personalData.name}] Paciente sem pacote - usando período mensal padrão`);
    
    const startDate = new Date(year, monthNum - 1, 1, 0, 0, 0, 0);
    const endDate = new Date(year, monthNum, 0, 23, 59, 59, 999);

    return { startDate, endDate };
  }

  /**
   * Formata período para exibição
   */
  static formatBillingPeriod(startDate: Date, endDate: Date): string {
    const start = startDate.toLocaleDateString('pt-BR');
    const end = endDate.toLocaleDateString('pt-BR');
    return `${start} a ${end}`;
  }

  /**
   * Verifica se uma data está dentro do período de faturamento
   */
  static isDateInPeriod(date: Date, startDate: Date, endDate: Date): boolean {
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);

    const normalizedStart = new Date(startDate);
    normalizedStart.setHours(0, 0, 0, 0);

    const normalizedEnd = new Date(endDate);
    normalizedEnd.setHours(23, 59, 59, 999);

    return normalizedDate >= normalizedStart && normalizedDate <= normalizedEnd;
  }

  /**
   * Calcula período mensal fixo (calendário) - usado para busca inicial de dados
   */
  static calculateMonthlyPeriod(month: string): { startDate: Date; endDate: Date } {
    const [year, monthNum] = month.split('-').map(Number);
    const startDate = new Date(year, monthNum - 1, 1, 0, 0, 0, 0);
    const endDate = new Date(year, monthNum, 0, 23, 59, 59, 999);
    return { startDate, endDate };
  }

  /**
   * Calcula período expandido para garantir captura de todas as sessões
   * Retorna do dia 1 do mês anterior ao último dia do mês seguinte
   * Útil para buscar dados que serão filtrados posteriormente por paciente
   */
  static calculateExpandedPeriod(month: string): { startDate: Date; endDate: Date } {
    const [year, monthNum] = month.split('-').map(Number);

    // Do dia 1 do mês anterior
    const startDate = new Date(year, monthNum - 2, 1, 0, 0, 0, 0);

    // Até o último dia do mês seguinte
    const endDate = new Date(year, monthNum + 1, 0, 23, 59, 59, 999);

    return { startDate, endDate };
  }

  /**
   * ✅ NOVA FUNÇÃO: Valida se os dados do pacote estão corretos
   */
// REPLACE
// REPLACE
  static validatePackageData(patient: UnifiedPatient): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!patient.financialProfile.hasPackage) {
      return { valid: true, errors: [] }; // Paciente sem pacote, não precisa validar
    }

    const packageInfo = patient.financialProfile.packageInfo;

    if (!packageInfo) {
      errors.push("Paciente marcado com pacote mas packageInfo está undefined");
      return { valid: false, errors };
    }

    if (!packageInfo.packagePaymentDate) {
      errors.push("packagePaymentDate não está definido");
    }

    if (!packageInfo.packageSessionsContracted || packageInfo.packageSessionsContracted <= 0) {
      errors.push(`sessionsContracted inválido: ${packageInfo.packageSessionsContracted}`);
    }

    if (!packageInfo.packagePrice || packageInfo.packagePrice <= 0) {
      errors.push(`price inválido: ${packageInfo.packagePrice}`);
    }

    if (!packageInfo.packageName) {
      errors.push("packageName não está definido");
    }

    if (patient.financialProfile.individualSessionPrice === undefined || patient.financialProfile.individualSessionPrice < 0) {
      errors.push(`individualSessionPrice inválido: ${patient.financialProfile.individualSessionPrice}`);
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * ✅ NOVA FUNÇÃO: Calcula e exibe resumo detalhado do faturamento
   */
// REPLACE
  static calculateDetailedBillingSummary(
    patient: UnifiedPatient,
    month: string,
    completedSessions: any[]
  ): {
    period: { start: string; end: string };
    sessionsContracted: number;
    sessionsCompleted: number;
    extraSessions: number;
    sessionsList: Array<{ date: string; status: string; title: string }>;
  } {
    const { startDate, endDate } = this.calculateBillingPeriod(patient, month);
    
    const sessionsContracted = patient.financialProfile.packageInfo?.packageSessionsContracted || 0;
    const sessionsCompleted = completedSessions.length;
    const extraSessions = Math.max(0, sessionsCompleted - sessionsContracted);

    const sessionsList = completedSessions.map(session => ({
      date: new Date(session.start || session.date).toLocaleDateString("pt-BR"),
      status: session.status,
      title: session.title || session.subject || "Sessão",
    }));

    console.log(`\n📊 ========== RESUMO DETALHADO DE FATURAMENTO ==========`);
    console.log(`👤 Paciente: ${patient.personalData.name}`);
    console.log(`📅 Período do pacote: ${this.formatBillingPeriod(startDate, endDate)}`);
    console.log(`📦 Sessões contratadas: ${sessionsContracted}`);
    console.log(`✅ Sessões completadas no período: ${sessionsCompleted}`);
    console.log(`➕ Sessões extras: ${extraSessions}`);
    console.log(`\n📋 Lista de sessões completadas:`);
    sessionsList.forEach((session, index) => {
      console.log(`   ${index + 1}. ${session.date} - ${session.status} - ${session.title}`);
    });
    console.log(`========================================================\n`);

    return {
      period: {
        start: startDate.toLocaleDateString("pt-BR"),
        end: endDate.toLocaleDateString("pt-BR"),
      },
      sessionsContracted,
      sessionsCompleted,
      extraSessions,
      sessionsList,
    };
  }
}
